import React, { useState, useEffect } from 'react';

import { useTheme } from '@mui/material/styles';
import Button from '@components/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@components/TextField';
import CopyToClipboard from '@aware/common/copy-to-clipboard/copy-to-clipboard';
import { useSession } from '@common/contexts/session';

import { getCookie, setCookie, clearCookie } from '@util/cookies';
import packageJSON from '@package';

import { isLocalDev, getOneClickURL } from './branch-indicator-utils';

const BranchIndicator = () => {
  const { zIndex } = useTheme();
  const [currentBranch, setCurrentBranch] = useState();
  const [expanded, setExpanded] = useState(true);
  const [showingDialog, setShowingDialog] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [devModeEnabled, setDevModeEnabled] = useState(false);

  const { organizationId, customerId, username } = useSession()?.currentUser || {};
  const { pathname } = window.location;

  const isOnlineTesting = /\/onlineTestingApi\/onlineTesting|^\/student\//i.test(pathname);

  useEffect(() => {
    const currentBranchCookie = getCookie('FrontEndBranchName');
    if (currentBranchCookie) {
      setNewBranchName(currentBranchCookie);
      setCurrentBranch(currentBranchCookie);
      setDevModeEnabled(true);
    } else if (isLocalDev()) {
      setCurrentBranch('localhost');
      setDevModeEnabled(true);
    }
  }, []);

  useEffect(() => {
    window.edpDevMode = () => {
      setDevModeEnabled(true);
    };

    return () => {
      delete window.edpDevMode;
    };
  }, []);

  const clearBranchCookie = () => {
    if (!currentBranch) {
      setDevModeEnabled(false);
      return;
    }

    if (currentBranch === 'localhost') {
      clearCookie('DevScriptsEnabled');
    } else {
      clearCookie('FrontEndBranchName');
    }
    window.location.reload();
  };

  const useLocalhost = () => {
    clearCookie('FrontEndBranchName');
    setCookie('DevScriptsEnabled', 'true');
    window.location.reload();
  };

  const useBranchName = () => {
    clearCookie('DevScriptsEnabled');
    setCookie('FrontEndBranchName', newBranchName);
    window.location.reload();
  };

  const fullUrl = getOneClickURL({
    username,
    organizationId,
    customerId: isOnlineTesting && customerId,
    branchName: newBranchName
  });

  // !window.top.Cypress so that branch indicator never shows up in Cypress tests
  return devModeEnabled && !window.top.Cypress ? (
    <>
      <Dialog open={showingDialog} fullWidth onClose={() => setShowingDialog(false)}>
        <DialogTitle>Front-end Development Branch</DialogTitle>
        <DialogContent>
          <TextField
            value={newBranchName}
            onChange={e => setNewBranchName(e.target.value)}
            fullWidth
            variant="filled"
            label="Branch name"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <div style={{ flexGrow: 1 }}>
            <CopyToClipboard content={fullUrl}>Copy As Url</CopyToClipboard>
          </div>
          <Button onClick={() => setShowingDialog(false)}>Cancel</Button>
          <Button onClick={useLocalhost}>Localhost Dev</Button>
          <Button color="primary" onClick={useBranchName} disabled={!newBranchName}>
            Set Branch
          </Button>
        </DialogActions>
      </Dialog>
      <ButtonGroup
        size="small"
        color="secondary"
        variant="contained"
        sx={{
          bottom: 70,
          position: 'fixed',
          left: 10,
          zIndex: zIndex.tooltip,
          '@media print': {
            display: 'none'
          }
        }}
      >
        {expanded && (
          <Button
            color="secondary"
            onClick={clearBranchCookie}
            sx={{
              px: 0.5,
              minWidth: 20
            }}
            aria-label="Clear Branch"
          >
            <ClearIcon />
          </Button>
        )}
        {expanded && (
          <Button color="secondary" onClick={() => setShowingDialog(true)}>
            {currentBranch || packageJSON.version}
          </Button>
        )}
        {expanded ? (
          <Button
            color="secondary"
            onClick={() => setExpanded(false)}
            aria-label="collapse"
          >
            <ChevronLeftIcon />
          </Button>
        ) : (
          <Button color="secondary" onClick={() => setExpanded(true)} aria-label="expand">
            <ChevronRightIcon />
          </Button>
        )}
      </ButtonGroup>
    </>
  ) : null;
};

export default BranchIndicator;
